import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Film, Play, Pause, RefreshCw, AlertCircle, ChevronDown, ChevronUp, Check, Copy } from 'lucide-react';
import { type Event } from 'nostr-tools';
import { useNostrQuery } from '../hooks/useNostrQuery';

// ══════════════════════════════════════════════════════════════════
// ██  CLIPS TEST PAGE — Isolated single-video playback diagnostic
// ══════════════════════════════════════════════════════════════════
//
// Purpose: Compare playback on this minimal page vs the Cash Clips feed.
// - Single video only, no feed, no comments, no tips, no overlays
// - Comprehensive logging of every HTMLMediaElement event
// - CORS/range-request probe results
// - Codec and MIME detection
// - Network error capture
//
// Navigate to: /clips-test

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isWebKit = typeof navigator !== 'undefined' && /AppleWebKit/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);

const VIDEO_EVENTS = [
  'loadstart', 'loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough',
  'play', 'playing', 'pause', 'stalled', 'waiting', 'error',
  'suspend', 'abort', 'emptied', 'ended', 'seeked', 'seeking',
] as const;

function mediaErrorName(code: number | undefined): string {
  switch (code) {
    case 1: return 'MEDIA_ERR_ABORTED';
    case 2: return 'MEDIA_ERR_NETWORK';
    case 3: return 'MEDIA_ERR_DECODE';
    case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED';
    default: return `UNKNOWN(${code})`;
  }
}

const VIDEO_RELAYS = ['wss://relay.divine.video', 'wss://relay.nostr.band'];
const VIDEO_KINDS = [22, 21, 34236, 34235];
const BCH_TAGS = ['bch', 'bitcoincash', 'cashclips'];

interface LogEntry {
  time: number;
  event: string;
  data?: Record<string, any>;
  level: 'info' | 'warn' | 'error';
}

function ensureVideoExtension(url: string): string {
  try {
    const u = new URL(url);
    if (/\.(mp4|webm|mov|m3u8|m4v)(\?.*)?$/i.test(u.pathname)) return url;
    if (/^\/[a-f0-9]{64}$/i.test(u.pathname)) {
      u.pathname += '.mp4';
      return u.toString();
    }
    return url;
  } catch { return url; }
}

function extractVideoUrl(event: Event): string | null {
  // From imeta tags
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    let url = '', m = '';
    for (const v of tag) {
      if (v.startsWith('url ')) url = v.slice(4);
      if (v.startsWith('m ')) m = v.slice(2);
    }
    if (m.startsWith('video/') || /\.(mp4|webm|mov|m3u8)(\?.*)?$/i.test(url)) return url;
  }
  // From url tag
  const urlTag = event.tags.find(t => t[0] === 'url');
  if (urlTag?.[1] && /\.(mp4|webm|mov|m3u8)(\?.*)?$/i.test(urlTag[1])) return urlTag[1];
  // From content
  const match = event.content.match(/(https?:\/\/[^\s]+\.(mp4|webm|mov|m3u8)(\?[^\s]*)?)/i);
  return match?.[1] || null;
}

export default function ClipsTestPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [playing, setPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [probeResult, setProbeResult] = useState<Record<string, any> | null>(null);
  const [probing, setProbing] = useState(false);
  const [showLogs, setShowLogs] = useState(true);
  const [copied, setCopied] = useState(false);
  const startTime = useRef(Date.now());
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Fetch one video from the BCH community to use as the test clip
  const { events: videoEvents, loading } = useNostrQuery({
    filters: [{ kinds: VIDEO_KINDS, '#t': BCH_TAGS, limit: 20 }],
    allRelays: true,
    extraRelays: VIDEO_RELAYS,
  });

  // Extract the first valid video URL
  const availableVideos = useMemo(() => {
    const results: { url: string; eventId: string }[] = [];
    const seen = new Set<string>();
    for (const ev of videoEvents) {
      const url = extractVideoUrl(ev);
      if (url && !seen.has(url)) {
        seen.add(url);
        results.push({ url: ensureVideoExtension(url), eventId: ev.id });
      }
    }
    return results.slice(0, 10);
  }, [videoEvents.length]);

  // Auto-select first video
  useEffect(() => {
    if (!videoUrl && availableVideos.length > 0) {
      setVideoUrl(availableVideos[0].url);
    }
  }, [availableVideos.length]);

  const addLog = (event: string, level: LogEntry['level'] = 'info', data?: Record<string, any>) => {
    const entry: LogEntry = { time: Date.now() - startTime.current, event, level, data };
    setLogs(prev => [...prev.slice(-200), entry]);
    // Also log to console
    const prefix = `[ClipsTest][${event}]`;
    if (level === 'error') console.error(prefix, data);
    else if (level === 'warn') console.warn(prefix, data);
    else console.log(prefix, data || '');
  };

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs.length]);

  // ── Attach event listeners when video URL changes ──
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    startTime.current = Date.now();
    setLogs([]);
    setPlaying(false);

    addLog('INIT', 'info', {
      url: videoUrl,
      userAgent: navigator.userAgent,
      isIOS,
      isWebKit,
      platform: navigator.platform,
    });

    // Set src directly (not via <source>)
    video.src = videoUrl;

    const handlers: Array<[string, EventListener]> = [];
    for (const eventName of VIDEO_EVENTS) {
      const handler = () => {
        const state: Record<string, any> = {
          readyState: video.readyState,
          networkState: video.networkState,
          currentTime: video.currentTime,
          duration: isNaN(video.duration) ? 'NaN' : video.duration,
          paused: video.paused,
          buffered: video.buffered.length > 0
            ? `${video.buffered.start(0).toFixed(2)}-${video.buffered.end(video.buffered.length - 1).toFixed(2)}s`
            : 'empty',
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        };

        if (eventName === 'error') {
          const err = video.error;
          addLog(eventName.toUpperCase(), 'error', {
            ...state,
            errorCode: err?.code,
            errorName: mediaErrorName(err?.code),
            errorMessage: err?.message || '(no message)',
          });
        } else if (eventName === 'stalled' || eventName === 'waiting' || eventName === 'abort') {
          addLog(eventName.toUpperCase(), 'warn', state);
        } else {
          addLog(eventName, 'info', state);
        }

        // Track playing state
        if (eventName === 'play' || eventName === 'playing') setPlaying(true);
        if (eventName === 'pause' || eventName === 'ended') setPlaying(false);
      };
      video.addEventListener(eventName, handler);
      handlers.push([eventName, handler]);
    }

    return () => {
      for (const [name, fn] of handlers) {
        video.removeEventListener(name, fn);
      }
    };
  }, [videoUrl]);

  // ── Probe URL ──
  const probeUrl = async (url: string) => {
    setProbing(true);
    setProbeResult(null);
    const result: Record<string, any> = {};

    try {
      // HEAD request to check headers
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(url, { method: 'HEAD', signal: controller.signal, mode: 'cors' });
      clearTimeout(timeout);

      result.status = res.status;
      result.contentType = res.headers.get('content-type') || '(missing)';
      result.acceptRanges = res.headers.get('accept-ranges') || '(missing)';
      result.contentLength = res.headers.get('content-length') || '(missing)';
      result.cors = res.headers.get('access-control-allow-origin') || '(missing)';
      result.cacheControl = res.headers.get('cache-control') || '(missing)';

      // Check for issues
      result.issues = [];
      if (!result.contentType.includes('video/'))
        result.issues.push(`Content-Type "${result.contentType}" — should be video/mp4`);
      if (result.acceptRanges === '(missing)' || result.acceptRanges === 'none')
        result.issues.push('No Accept-Ranges header — iOS Safari REQUIRES byte-range support');
      if (result.cors === '(missing)')
        result.issues.push('No CORS header (Access-Control-Allow-Origin)');
      if (result.contentType.includes('webm') || result.contentType.includes('vp9'))
        result.issues.push('WebM/VP9 codec — NOT supported on iOS Safari');

      // Try a range request
      try {
        const rangeRes = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-1' },
          signal: AbortSignal.timeout(5000),
          mode: 'cors',
        });
        result.rangeStatus = rangeRes.status;
        result.rangeSupported = rangeRes.status === 206;
        if (rangeRes.status !== 206) {
          result.issues.push(`Range request returned ${rangeRes.status} — expected 206 Partial Content`);
        }
        const rangeContentType = rangeRes.headers.get('content-type');
        if (rangeContentType) result.rangeContentType = rangeContentType;
      } catch (rangeErr) {
        result.rangeFailed = true;
        result.issues.push('Range request failed: ' + (rangeErr instanceof Error ? rangeErr.message : String(rangeErr)));
      }

      addLog('PROBE_COMPLETE', result.issues.length > 0 ? 'warn' : 'info', result);
    } catch (err) {
      result.error = err instanceof Error ? err.message : String(err);
      result.issues = ['Probe failed: ' + result.error];
      addLog('PROBE_FAILED', 'error', result);
    }

    setProbeResult(result);
    setProbing(false);
  };

  useEffect(() => {
    if (videoUrl) probeUrl(videoUrl);
  }, [videoUrl]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.pause();
    } else {
      video.muted = false;
      video.play().then(() => setPlaying(true)).catch(err => {
        addLog('PLAY_FAILED', 'error', { error: err.message });
        // Retry muted
        video.muted = true;
        video.play().catch(err2 => {
          addLog('PLAY_FAILED_MUTED', 'error', { error: err2.message });
        });
      });
    }
  };

  const handleLoadCustomUrl = () => {
    const url = customUrl.trim();
    if (url) {
      setVideoUrl(ensureVideoExtension(url));
      setCustomUrl('');
    }
  };

  const copyLogs = () => {
    const text = logs.map(l =>
      `[${l.time}ms] [${l.level.toUpperCase()}] ${l.event}${l.data ? ' ' + JSON.stringify(l.data) : ''}`
    ).join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to="/clips" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-base font-bold flex items-center gap-2">
              <Film className="w-4 h-4 text-primary" /> Clips Test Page
            </h1>
            <p className="text-[10px] text-muted-foreground">
              Isolated single-video playback diagnostic
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Device info */}
        <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-1">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Device Info</p>
          <p className="text-xs font-mono break-all">{navigator.userAgent}</p>
          <div className="flex flex-wrap gap-2 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isIOS ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-muted text-muted-foreground'}`}>
              {isIOS ? 'iOS' : 'Not iOS'}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isWebKit ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400' : 'bg-muted text-muted-foreground'}`}>
              {isWebKit ? 'WebKit/Safari' : 'Not WebKit'}
            </span>
          </div>
        </div>

        {/* Video selector */}
        {availableVideos.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Select a test video:</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {availableVideos.map((v, i) => (
                <button
                  key={v.eventId}
                  onClick={() => setVideoUrl(v.url)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-mono break-all transition-colors ${
                    videoUrl === v.url
                      ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  }`}
                >
                  #{i + 1}: {v.url.length > 70 ? v.url.slice(0, 35) + '...' + v.url.slice(-30) : v.url}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom URL */}
        <div className="flex gap-2">
          <input
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleLoadCustomUrl(); }}
            placeholder="Or paste a video URL..."
            className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={handleLoadCustomUrl}
            disabled={!customUrl.trim()}
            className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
          >
            Load
          </button>
        </div>

        {loading && availableVideos.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Loading videos from relays...
          </div>
        )}

        {/* VIDEO PLAYER — single, minimal, no overlays */}
        {videoUrl && (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[60vh]">
            {/* No crossOrigin — divine.video lacks CORS headers,
                which causes iOS Safari to block video data after metadata.
                crossOrigin is only needed for canvas/WebGL access, not playback. */}
            <video
              ref={videoRef}
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-contain"
              onClick={togglePlay}
            />
            {/* Minimal play button */}
            {!playing && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white translate-x-0.5" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Probe results */}
        {(probeResult || probing) && (
          <div className="rounded-xl border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold flex items-center gap-1.5">
                <RefreshCw className={`w-3 h-3 ${probing ? 'animate-spin text-primary' : 'text-muted-foreground'}`} />
                URL Probe
              </p>
              {videoUrl && (
                <button
                  onClick={() => probeUrl(videoUrl)}
                  disabled={probing}
                  className="text-[10px] text-primary hover:underline disabled:opacity-50"
                >
                  Re-probe
                </button>
              )}
            </div>
            {probeResult && (
              <>
                <div className="grid grid-cols-2 gap-1 text-[11px]">
                  <span className="text-muted-foreground">Content-Type:</span>
                  <span className="font-mono">{probeResult.contentType}</span>
                  <span className="text-muted-foreground">Accept-Ranges:</span>
                  <span className="font-mono">{probeResult.acceptRanges}</span>
                  <span className="text-muted-foreground">Content-Length:</span>
                  <span className="font-mono">{probeResult.contentLength}</span>
                  <span className="text-muted-foreground">CORS:</span>
                  <span className="font-mono">{probeResult.cors}</span>
                  <span className="text-muted-foreground">Range Support:</span>
                  <span className="font-mono">{probeResult.rangeSupported ? 'Yes (206)' : probeResult.rangeFailed ? 'Failed' : `No (${probeResult.rangeStatus})`}</span>
                </div>
                {probeResult.issues?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {probeResult.issues.map((issue: string, i: number) => (
                      <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                        <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>{issue}</span>
                      </div>
                    ))}
                  </div>
                )}
                {probeResult.issues?.length === 0 && (
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> All checks passed
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Event log */}
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            onClick={() => setShowLogs(p => !p)}
            className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 hover:bg-muted transition-colors"
          >
            <span className="text-xs font-semibold flex items-center gap-1.5">
              Video Event Log ({logs.length})
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); copyLogs(); }}
                className="text-[10px] text-primary hover:underline flex items-center gap-1"
              >
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              {showLogs ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
          </button>
          {showLogs && (
            <div className="max-h-[300px] overflow-y-auto p-2 space-y-0.5 bg-black/5 dark:bg-white/5">
              {logs.length === 0 && (
                <p className="text-[11px] text-muted-foreground text-center py-4">
                  No events yet. Select a video above.
                </p>
              )}
              {logs.map((log, i) => (
                <div
                  key={i}
                  className={`text-[10px] font-mono px-2 py-1 rounded ${
                    log.level === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                    log.level === 'warn' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' :
                    'text-muted-foreground'
                  }`}
                >
                  <span className="text-muted-foreground/60">{log.time}ms</span>
                  {' '}
                  <span className="font-semibold">{log.event}</span>
                  {log.data && (
                    <span className="ml-1 break-all">
                      {JSON.stringify(log.data, null, 0).slice(0, 200)}
                    </span>
                  )}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground">How to use this page:</p>
          <ol className="text-[11px] text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Open this page on the iOS device experiencing issues</li>
            <li>Wait for a video to load, then tap to play</li>
            <li>If the video goes black, check the event log above</li>
            <li>Look for ERROR or STALLED events</li>
            <li>Check the URL Probe for CORS/range-request issues</li>
            <li>Copy the log and compare with the same video on desktop</li>
            <li>Then visit <Link to="/clips" className="text-primary hover:underline">/clips</Link> and compare behavior</li>
          </ol>
          <div className="mt-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-[11px] text-primary font-medium">Key iOS failure patterns to look for:</p>
            <ul className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
              <li>- loadstart fires but loadedmetadata never fires = network/CORS issue</li>
              <li>- MEDIA_ERR_SRC_NOT_SUPPORTED = wrong codec (WebM/VP9) or bad Content-Type</li>
              <li>- MEDIA_ERR_NETWORK = missing byte-range support or CORS failure</li>
              <li>- MEDIA_ERR_DECODE = corrupted file or unsupported codec variant</li>
              <li>- stalled event + no further progress = server not supporting range requests</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

